import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Sparkles, TrendingUp, Clock, Users } from 'lucide-react';
import { formatDate, getInitials } from '../lib/utils';
import api from '../lib/api';

export default function ProjectOverviewTab({ project }) {
  const [events, setEvents] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, [project.id]);

  const loadData = async () => {
    try {
      const [eventsRes, insightsRes] = await Promise.all([
        api.get('/events', { params: { project_id: project.id, limit: 5 } }),
        api.get('/insights', { params: { project_id: project.id, limit: 3 } })
      ]);
      setEvents(eventsRes.data);
      setInsights(insightsRes.data);
    } catch (error) {
      console.error('Failed to load overview data:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="project-overview-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Clock className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Last activity</p>
              <p className="font-semibold">{events.length > 0 ? formatDate(events[0].timestamp) : 'No activity'}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <TrendingUp className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">Total updates</p>
              <p className="font-semibold">{events.length}</p>
            </div>
          </div>
        </Card>

        <Card className="p-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Sparkles className="h-5 w-5 text-primary" />
            </div>
            <div>
              <p className="text-sm text-muted-foreground">AI Insights</p>
              <p className="font-semibold">{insights.length}</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Recent Insights */}
      {insights.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold">Recent Insights</h2>
            <Link to={`/projects/${project.id}/insights`}>
              <Button variant="ghost" size="sm">View all</Button>
            </Link>
          </div>
          <div className="space-y-4">
            {insights.map((insight) => (
              <Card key={insight.id} className="p-4">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-violet-500 to-indigo-500 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-white" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold mb-1">{insight.title}</h3>
                    <p className="text-sm text-muted-foreground mb-2">{insight.content}</p>
                    <span className="text-xs text-muted-foreground">{formatDate(insight.created_at)}</span>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recent Activity */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold">Recent Activity</h2>
          <Link to={`/projects/${project.id}/feed`}>
            <Button variant="ghost" size="sm">View all</Button>
          </Link>
        </div>
        
        {events.length === 0 ? (
          <Card className="p-8 text-center">
            <p className="text-muted-foreground">No activity yet. Connect sources to start seeing updates.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {events.slice(0, 5).map((event) => (
              <Card key={event.id} className="p-4">
                <div className="flex items-start gap-3">
                  <Avatar>
                    <AvatarFallback>{getInitials(event.author_name || 'U')}</AvatarFallback>
                  </Avatar>
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="font-medium text-sm">{event.author_name}</span>
                      <span className="text-xs text-muted-foreground">{formatDate(event.timestamp)}</span>
                    </div>
                    <p className="text-sm">{event.title}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
